import { downloadCsv, formatDate, formatShares } from '../lib/format';

/** How coarsely the filer tagged its cover-page share count. */
const SCALE_LABEL = { 1000: 'thousands', 1000000: 'millions', 1000000000: 'billions' };

/**
 * One row per 10-K/10-Q, showing where each share count came from.
 *
 * The two date columns are deliberately separate and both are shown. A 10-Q for the
 * quarter ended March 31 typically states its share count as of early May — "the
 * number of shares outstanding as of May 7, 2026 was X". Those are five weeks apart,
 * and the market-cap series is driven by the later one, so collapsing them into a
 * single "date" column would hide the thing most likely to be misread.
 */
export default function FilingsTable({ ticker, rows, windowStart }) {
  const anySplit = rows.some((r) => r.splitFactor != null && r.splitFactor !== 1);
  const anyMultiClass = rows.some((r) => r.classes && r.classes.length > 1);

  function exportCsv() {
    const headers = [
      'Form', 'Period ended', 'Filed', 'Shares outstanding as of', 'Shares outstanding (as filed)',
      'Split factor', 'Shares (split-adjusted)', 'Share class', 'All classes total', 'Source', 'Filing URL',
    ];
    downloadCsv(
      `${ticker}-filings.csv`,
      headers,
      rows.map((r) => [
        r.form, r.periodEnd ?? '', r.filingDate, r.asOfDate ?? '', r.shares ?? '',
        r.splitFactor ?? '', r.adjustedShares ?? '', r.shareClass ?? '',
        r.totalAllClasses ?? '', r.source ?? r.error ?? '', r.documentUrl,
      ]),
    );
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Shares outstanding by filing</h2>
        <p className="card-note">
          {rows.length} filings · each count is dated as of the cover page, not the period end
        </p>
        <div className="card-actions">
          <button type="button" className="btn-quiet" onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Form</th>
              <th>Period ended</th>
              <th>Filed</th>
              <th>Shares as of</th>
              <th className="num">Shares outstanding</th>
              {anySplit && <th className="num">Split-adjusted</th>}
              <th>Share class</th>
              {anyMultiClass && <th className="num">All classes</th>}
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const outsideWindow = windowStart && row.asOfDate && row.asOfDate < windowStart;
              return (
                <tr key={row.accessionNumber} className={outsideWindow ? 'row-outside' : undefined}>
                  <td>
                    <a href={row.documentUrl} target="_blank" rel="noopener noreferrer">
                      <span className="badge badge-form">{row.form}</span>
                    </a>{' '}
                    <a
                      href={row.indexUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hint"
                      title="EDGAR filing index"
                    >
                      index
                    </a>
                  </td>
                  <td className="mono">{formatDate(row.periodEnd)}</td>
                  <td className="mono">{formatDate(row.filingDate)}</td>
                  <td className="mono">
                    {formatDate(row.asOfDate)}
                    {row.asOfInferred && (
                      <>
                        {' '}
                        <span className="badge badge-warn" title="The cover page carried no usable as-of instant; this date was inferred from the filing.">
                          inferred
                        </span>
                      </>
                    )}
                  </td>
                  <td className="num">
                    {row.shares == null ? (
                      <span className="hint">not found</span>
                    ) : (
                      <>
                        {formatShares(row.shares)}
                        {row.unitScale > 1 && (
                          <div className="hint" title="The filer tagged this fact at a coarse scale, so the count is rounded at source.">
                            rounded to {SCALE_LABEL[row.unitScale] ?? `${row.unitScale}s`}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  {anySplit && (
                    <td className="num">
                      {row.adjustedShares == null ? (
                        '—'
                      ) : row.splitFactor === 1 ? (
                        <span className="hint">no change</span>
                      ) : (
                        <>
                          {formatShares(row.adjustedShares)}
                          <div className="hint">
                            ×{row.splitFactor < 1 ? row.splitFactor.toPrecision(3) : row.splitFactor}
                          </div>
                        </>
                      )}
                    </td>
                  )}
                  <td>
                    {row.shareClass ?? (row.shares == null ? '—' : 'Common stock')}
                    {row.classAmbiguous && (
                      <>
                        {' '}
                        <span className="badge badge-warn" title="This filing tags several classes but no trading symbol, so the traded class was assumed.">
                          assumed
                        </span>
                      </>
                    )}
                  </td>
                  {anyMultiClass && (
                    <td className="num">
                      {row.classes && row.classes.length > 1 ? (
                        <span title={row.classes.map((c) => `${c.label}: ${formatShares(c.shares)}`).join('\n')}>
                          {formatShares(row.totalAllClasses)}
                        </span>
                      ) : (
                        <span className="hint">—</span>
                      )}
                    </td>
                  )}
                  <td>
                    {row.error ? (
                      <span className="badge badge-warn" title={row.error}>
                        unreadable
                      </span>
                    ) : (
                      <>
                        <span className="badge">{row.source}</span>
                        {row.superseded && (
                          <>
                            {' '}
                            <span className="badge" title="A later filing reports the same as-of date; that one is used in the chart.">
                              superseded
                            </span>
                          </>
                        )}
                        {row.lowConfidence && (
                          <>
                            {' '}
                            <span className="badge badge-warn" title={row.context}>
                              low confidence
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card-foot">
        <p className="hint" style={{ margin: 0 }}>
          <strong>Reading this table.</strong> “Period ended” is the quarter or year the report
          covers. “Shares as of” is the date the filing itself states the count for — usually a few
          weeks after the period end, on the cover page. On a 20-F or 40-F the two coincide: foreign
          issuers state the count as of the fiscal year end rather than the cover date. The chart
          applies each count from its <em>shares as of</em> date forward, until the next filing.
          {windowStart && ' Greyed rows sit before the selected window and are included only so the first day of the chart has a count in effect.'}
        </p>
      </div>
    </section>
  );
}
